import { useState, useEffect } from "react";
import { enviarImagemCidade } from "../../../services/cidades";
import { ConfirmDialog, MemberDialog } from "../Dialogs";
import FormActions from "../FormActions/FormActions";
import {
  getImageUrl,
  getVideoEmbedUrl,
  isSupportedVideoUrl,
} from "../../../utils/media";
import { normalizeCityIndicators } from "../../../utils/cityIndicators";
import { slugify } from "../../../utils/slug";
import { PATHS } from "../../../routes/paths";
import "./style.css";

const MAX_IMAGE_SIZE = 15 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHATSAPP_REGEX = /^(?:\d{10,11}|55\d{10,11})$/;
const PHONE_CHARACTERS_REGEX = /^[+\d\s().-]+$/;

const isVideoMediaUrl = (url) =>
  /youtube\.com|youtu\.be|instagram\.com/i.test(String(url || ""));

const normalizeSlide = (slide) => {
  const normalized =
    typeof slide === "string" ? { url: slide, alt: "" } : { ...slide };

  return {
    ...normalized,
    url: normalized.url || "",
    alt: normalized.alt || "",
    mediaType: isVideoMediaUrl(normalized.url) ? "video" : "image",
  };
};

const EMPTY_CIDADE = {
  name: "",
  city: "",
  state: "",
  description: "",
  helpText: "",
  active: true,
  menuOrder: 0,
  presentation: { title: "", subtitle: "", description: "" },
  slideshow: [],
  indicators: [],
  indicatorsImage: "",
  team: [],
  partnerships: [],
  contact_info: {
    address: "",
    phone: "",
    whatsapp: "",
    email: "",
    openingHours: "",
    website: "",
  },
};

export default function CidadeForm({ initialValue, onSubmit, onCancel, submitting }) {
  const isCreating = !initialValue;
  const [form, setForm] = useState(EMPTY_CIDADE);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadingParceriaIndex, setUploadingParceriaIndex] = useState(null);
  const [uploadingIndicatorsImage, setUploadingIndicatorsImage] =
    useState(false);
  const [error, setError] = useState("");
  const [memberEditor, setMemberEditor] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);

  useEffect(() => {
    setForm(
      initialValue
        ? {
            name: initialValue.name || "",
            city: initialValue.city || "",
            state: initialValue.state || "",
            description: initialValue.description || "",
            helpText: initialValue.helpText || "",
            active: initialValue.active !== false,
            menuOrder: Number.isFinite(Number(initialValue.menuOrder))
              ? Number(initialValue.menuOrder)
              : 0,
            presentation: {
              ...EMPTY_CIDADE.presentation,
              ...initialValue.presentation,
            },
            contact_info: {
              ...EMPTY_CIDADE.contact_info,
              ...initialValue.contact_info,
            },
            slideshow: (initialValue.slideshow || []).map(normalizeSlide),
            indicators: normalizeCityIndicators(initialValue.indicators),
            indicatorsImage: initialValue.indicatorsImage || "",
            team: (initialValue.team || []).map((member, index) => ({
              ...member,
              order: index,
            })),
            partnerships: (initialValue.partnerships || []).map(
              (partnership, index) => ({
                ...partnership,
                order: index,
              }),
            ),
          }
        : EMPTY_CIDADE,
    );
    setError("");
  }, [initialValue]);

  const setField = (path, value) => {
    setForm((prev) => {
      const keys = path.split(".");
      if (keys.length === 1) return { ...prev, [keys[0]]: value };
      const [outer, inner] = keys;
      return { ...prev, [outer]: { ...prev[outer], [inner]: value } };
    });
  };

  const addSlide = () =>
    setForm((prev) => ({
      ...prev,
      slideshow: [
        ...prev.slideshow,
        { url: "", alt: "", mediaType: "image" },
      ],
    }));
  const updateSlideFields = (index, fields) =>
    setForm((prev) => ({
      ...prev,
      slideshow: prev.slideshow.map((s, i) =>
        i === index ? { ...s, ...fields } : s,
      ),
    }));
  const updateSlide = (index, field, value) =>
    updateSlideFields(index, { [field]: value });
  const removeSlide = (index) =>
    setForm((prev) => ({
      ...prev,
      slideshow: prev.slideshow.filter((_, i) => i !== index),
    }));

  const uploadSlideImage = async (index, file) => {
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Selecione uma imagem JPG ou PNG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError("A imagem deve ter no máximo 15 MB.");
      return;
    }

    setUploadingIndex(index);
    setError("");

    try {
      const uploadedImage = await enviarImagemCidade(file);
      updateSlideFields(index, {
        url: uploadedImage.url,
        mediaType: "image",
      });
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.error ||
          "Não foi possível enviar a imagem. Tente novamente.",
      );
    } finally {
      setUploadingIndex(null);
    }
  };

  const updateIndicator = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      indicators: prev.indicators.map((it, i) =>
        i === index ? { ...it, [field]: value } : it,
      ),
    }));

  const saveMember = (member) => {
    const editor = memberEditor;
    if (!editor) return;

    setForm((prev) => {
      const savedMember =
        editor.index === null
          ? member
          : { ...prev.team[editor.index], ...member };
      const nextTeam =
        editor.index === null
          ? [...prev.team, savedMember]
          : prev.team.map((currentMember, index) =>
              index === editor.index ? savedMember : currentMember,
            );

      return {
        ...prev,
        team: nextTeam.map((currentMember, index) => ({
          ...currentMember,
          order: index,
        })),
      };
    });
    setMemberEditor(null);
  };

  const removeMember = () => {
    if (memberToRemove === null) return;

    setForm((prev) => ({
      ...prev,
      team: prev.team
        .filter((_, index) => index !== memberToRemove)
        .map((member, index) => ({ ...member, order: index })),
    }));
    setMemberToRemove(null);
  };

  const uploadIndicatorsImage = async (file) => {
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Selecione uma imagem JPG ou PNG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError("A imagem deve ter no máximo 15 MB.");
      return;
    }

    setUploadingIndicatorsImage(true);
    setError("");

    try {
      const uploadedImage = await enviarImagemCidade(file);
      setField("indicatorsImage", uploadedImage.url);
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.error ||
          "Não foi possível enviar a imagem. Tente novamente.",
      );
    } finally {
      setUploadingIndicatorsImage(false);
    }
  };

  const addPartnership = () =>
    setForm((prev) => ({
      ...prev,
      partnerships: [
        ...prev.partnerships,
        { name: "", logoUrl: "", redirectUrl: "" },
      ],
    }));

  const updatePartnership = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      partnerships: prev.partnerships.map((partnership, i) =>
        i === index ? { ...partnership, [field]: value } : partnership,
      ),
    }));

  const removePartnership = (index) =>
    setForm((prev) => ({
      ...prev,
      partnerships: prev.partnerships.filter((_, i) => i !== index),
    }));

  const uploadPartnershipLogo = async (index, file) => {
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Selecione uma imagem JPG ou PNG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError("A imagem deve ter no máximo 15 MB.");
      return;
    }

    setUploadingParceriaIndex(index);
    setError("");

    try {
      const uploadedImage = await enviarImagemCidade(file);
      updatePartnership(index, "logoUrl", uploadedImage.url);
    } catch (uploadError) {
      setError(
        uploadError?.response?.data?.error ||
          "Não foi possível enviar a imagem. Tente novamente.",
      );
    } finally {
      setUploadingParceriaIndex(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredValues = [
      form.description,
      form.presentation.title,
      form.presentation.description,
      ...(isCreating ? [form.name] : []),
    ];

    if (requiredValues.some((value) => !String(value || "").trim())) {
      setError("Preencha todos os campos obrigatórios antes de salvar.");
      return;
    }

    const whatsappDigits = form.contact_info.whatsapp.replace(/\D/g, "");
    if (
      form.contact_info.whatsapp &&
      (!PHONE_CHARACTERS_REGEX.test(form.contact_info.whatsapp) ||
        !WHATSAPP_REGEX.test(whatsappDigits))
    ) {
      setError(
        "Informe um WhatsApp válido com DDD, por exemplo: (35) 99999-9999.",
      );
      return;
    }

    const email = form.contact_info.email.trim();
    if (email && !EMAIL_REGEX.test(email)) {
      setError("Informe um endereço de e-mail válido.");
      return;
    }

    const invalidVideo = form.slideshow.find((slide) => {
      const url = String(slide.url || "").trim();
      if (!url) return false;
      return slide.mediaType === "video" && !isSupportedVideoUrl(url);
    });

    if (invalidVideo) {
      setError(
        "Use um link válido de vídeo do YouTube ou de uma publicação do Instagram.",
      );
      return;
    }

    const invalidIndicator = form.indicators.some(
      (indicator) =>
        !String(indicator.value || "").trim() ||
        !String(indicator.label || "").trim(),
    );

    if (invalidIndicator) {
      setError("Informe o valor e o rótulo de todos os indicadores.");
      return;
    }

    const invalidMember = form.team.some(
      (member) =>
        !String(member.name || "").trim() ||
        !String(member.role || "").trim(),
    );

    if (invalidMember) {
      setError("Informe o nome e o cargo de todos os membros da equipe.");
      return;
    }

    const invalidPartnership = form.partnerships.some(
      (partnership) => !String(partnership.name || "").trim(),
    );

    if (invalidPartnership) {
      setError("Informe o nome de todas as parcerias.");
      return;
    }

    const payload = {
      ...(isCreating
        ? { name: form.name.trim(), city: form.city.trim() }
        : {}),
      active: form.active,
      menuOrder: Number.isFinite(Number(form.menuOrder))
        ? Number(form.menuOrder)
        : 0,
      state: form.state.trim(),
      description: form.description.trim(),
      helpText: form.helpText.trim(),
      presentation: {
        title: form.presentation.title.trim(),
        subtitle: form.presentation.subtitle.trim(),
        description: form.presentation.description.trim(),
      },
      slideshow: form.slideshow
        .filter((slide) => String(slide.url || "").trim())
        .map((slide, index) => ({
          url: String(slide.url).trim(),
          alt: String(slide.alt || "").trim(),
          order: index,
        })),
      indicatorsImage: String(form.indicatorsImage || "").trim(),
      indicators: form.indicators.map((indicator, index) => ({
        value: String(indicator.value).trim(),
        label: String(indicator.label).trim(),
        description: String(indicator.description || "").trim(),
        order: index,
      })),
      team: form.team.map((member, index) => ({
        ...(member._id ? { _id: member._id } : {}),
        name: String(member.name).trim(),
        role: String(member.role).trim(),
        photoUrl: String(member.photoUrl || "").trim(),
        photoPublicId: String(member.photoPublicId || "").trim(),
        order: index,
      })),
      teamConfigured: true,
      partnerships: form.partnerships.map((partnership, index) => ({
        name: String(partnership.name).trim(),
        logoUrl: String(partnership.logoUrl || "").trim(),
        redirectUrl: String(partnership.redirectUrl || "").trim(),
        order: index,
      })),
      contact_info: Object.fromEntries(
        Object.entries(form.contact_info).map(([key, value]) => [
          key,
          String(value || "").trim(),
        ]),
      ),
    };

    setError("");
    onSubmit(payload);
  };

  return (
    <form className="cidade-form" onSubmit={handleSubmit}>
      <fieldset className="cidade-form__secao">
        <legend>Identificação do núcleo</legend>

        {isCreating && (
          <div className="cidade-form__caixa-titulo">
            <div className="cidade-form__linha">
              <label className="cidade-form__campo">
                <span>Nome da cidade *</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({ ...prev, name: value, city: value }));
                  }}
                  placeholder="Ex: Lavras"
                  required
                />
              </label>
            </div>
            <span className="cidade-form__slug-preview">
              URL pública: {PATHS.ondeEstamos}/
              {slugify(form.name) || "..."}
            </span>
          </div>
        )}

        <label className="cidade-form__campo">
          <span>Estado (UF)</span>
          <input
            type="text"
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
            placeholder="Ex: Minas Gerais"
          />
        </label>

        <label className="cidade-form__ativo">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setField("active", e.target.checked)}
          />
          Cidade visível no site (ativa)
        </label>

        <label className="cidade-form__campo">
          <span>Ordem no menu "Onde Estamos"</span>
          <input
            type="number"
            value={form.menuOrder}
            onChange={(e) => setField("menuOrder", e.target.value)}
          />
        </label>

        <label className="cidade-form__campo">
          <span>Título do núcleo *</span>
          <input
            type="text"
            value={form.presentation.title}
            onChange={(e) => setField("presentation.title", e.target.value)}
            placeholder="Ex: Núcleo 7 Geração - ProSer"
            required
          />
        </label>
      </fieldset>

      <label className="cidade-form__campo">
        <span>Quem mantém este núcleo? *</span>
        <textarea
          rows={5}
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          placeholder="Descreva a instituição e as organizações que mantêm este núcleo"
          required
        />
      </label>

      <fieldset className="cidade-form__secao">
        <legend>História e Atuação</legend>
        <label className="cidade-form__campo">
          <span>História do núcleo *</span>
          <textarea
            rows={8}
            value={form.presentation.description}
            onChange={(e) =>
              setField("presentation.description", e.target.value)
            }
            required
          />
        </label>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Imagens e vídeos</legend>
        {form.slideshow.length === 0 && (
          <p className="cidade-form__vazio">Nenhuma mídia adicionada.</p>
        )}
        {form.slideshow.map((slide, i) => (
          <div key={i} className="cidade-form__media-item">
            <div className="cidade-form__media-preview">
              {slide.mediaType === "video" && getVideoEmbedUrl(slide.url) ? (
                <iframe
                  src={getVideoEmbedUrl(slide.url)}
                  title={slide.alt || `Vídeo ${i + 1}`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : slide.mediaType === "image" && getImageUrl(slide.url) ? (
                <img
                  src={getImageUrl(slide.url)}
                  alt={slide.alt || "Prévia da mídia"}
                />
              ) : (
                <span>Prévia da mídia</span>
              )}
            </div>

            <div className="cidade-form__media-fields">
              <div
                className="cidade-form__media-mode"
                role="group"
                aria-label={`Tipo da mídia ${i + 1}`}
              >
                <button
                  type="button"
                  className={slide.mediaType === "image" ? "is-active" : ""}
                  onClick={() =>
                    updateSlideFields(i, {
                      mediaType: "image",
                      url: slide.mediaType === "image" ? slide.url : "",
                    })
                  }
                >
                  Imagem
                </button>
                <button
                  type="button"
                  className={slide.mediaType === "video" ? "is-active" : ""}
                  onClick={() =>
                    updateSlideFields(i, {
                      mediaType: "video",
                      url: slide.mediaType === "video" ? slide.url : "",
                    })
                  }
                >
                  Vídeo
                </button>
              </div>

              {slide.mediaType === "video" && (
                <label className="cidade-form__video-field">
                  <span>Link do vídeo</span>
                  <input
                    type="url"
                    value={slide.url}
                    onChange={(e) => updateSlide(i, "url", e.target.value)}
                    placeholder="YouTube ou Instagram"
                  />
                </label>
              )}
              <input
                type="text"
                value={slide.alt}
                onChange={(e) => updateSlide(i, "alt", e.target.value)}
                placeholder="Texto alternativo (acessibilidade)"
              />
              <div className="cidade-form__media-actions">
                {slide.mediaType === "image" && (
                  <label className="cidade-form__upload">
                    <span>
                      {uploadingIndex === i
                        ? "Enviando..."
                        : slide.url
                          ? "Substituir imagem"
                          : "Carregar imagem"}
                    </span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      disabled={uploadingIndex !== null}
                      onChange={(event) => {
                        uploadSlideImage(i, event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />
                  </label>
                )}
                <button
                  type="button"
                  className="cidade-form__remover"
                  onClick={() => removeSlide(i)}
                  aria-label={`Remover mídia ${i + 1}`}
                >
                  {slide.mediaType === "video" ? "Remover vídeo" : "Remover imagem"}
                </button>
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="cidade-form__adicionar" onClick={addSlide}>
          + Adicionar mídia
        </button>
        <small className="cidade-form__ajuda">
          Imagens JPG ou PNG de até 15 MB. Vídeos somente por link do YouTube ou
          Instagram.
        </small>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Indicadores</legend>
        <div className="cidade-form__media-preview">
          {getImageUrl(form.indicatorsImage) ? (
            <img
              src={getImageUrl(form.indicatorsImage)}
              alt="Prévia da imagem de fundo dos indicadores"
            />
          ) : (
            <span>Sem imagem de fundo</span>
          )}
        </div>
        <label className="cidade-form__upload">
          <span>
            {uploadingIndicatorsImage
              ? "Enviando..."
              : form.indicatorsImage
                ? "Substituir imagem de fundo"
                : "Carregar imagem de fundo"}
          </span>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            disabled={uploadingIndicatorsImage}
            onChange={(event) => {
              uploadIndicatorsImage(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </label>
        {form.indicators.map((item, i) => (
          <div key={i} className="cidade-form__linha-lista cidade-form__linha-lista--indicador">
            <input
              type="text"
              value={item.value}
              onChange={(e) => updateIndicator(i, "value", e.target.value)}
              placeholder="Valor (ex: 300)"
              required
            />
            <input
              type="text"
              value={item.label}
              onChange={(e) => updateIndicator(i, "label", e.target.value)}
              placeholder="Rótulo (ex: atendidos)"
              required
            />
            <input
              type="text"
              value={item.description}
              onChange={(e) =>
                updateIndicator(i, "description", e.target.value)
              }
              placeholder="Descrição adicional (opcional)"
            />
          </div>
        ))}
        <small className="cidade-form__ajuda">
          As páginas de cidade exibem sempre estes três indicadores.
        </small>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Parcerias</legend>
        {form.partnerships.length === 0 && (
          <p className="cidade-form__vazio">Nenhuma parceria adicionada.</p>
        )}
        <div className="cidade-form__parcerias">
          {form.partnerships.map((partnership, i) => (
            <div key={i} className="cidade-form__card-parceria">
              {getImageUrl(partnership.logoUrl) ? (
                <img
                  src={getImageUrl(partnership.logoUrl)}
                  alt={partnership.name || "Logo da parceria"}
                  className="cidade-form__parceria-logo"
                />
              ) : (
                <span className="cidade-form__vazio">Sem logo</span>
              )}
              <input
                type="text"
                value={partnership.name}
                onChange={(e) =>
                  updatePartnership(i, "name", e.target.value)
                }
                placeholder="Nome do parceiro *"
                required
              />
              <input
                type="url"
                value={partnership.redirectUrl}
                onChange={(e) =>
                  updatePartnership(i, "redirectUrl", e.target.value)
                }
                placeholder="Link (opcional)"
              />
              <label className="cidade-form__upload">
                <span>
                  {uploadingParceriaIndex === i
                    ? "Enviando..."
                    : partnership.logoUrl
                      ? "Substituir logo"
                      : "Carregar logo"}
                </span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  disabled={uploadingParceriaIndex !== null}
                  onChange={(event) => {
                    uploadPartnershipLogo(i, event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
              </label>
              <button
                type="button"
                className="cidade-form__remover"
                onClick={() => removePartnership(i)}
                aria-label={`Remover parceria ${i + 1}`}
              >
                Remover
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="cidade-form__adicionar"
          onClick={addPartnership}
        >
          + Adicionar parceria
        </button>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Equipe</legend>
        {form.team.length === 0 && (
          <p className="cidade-form__vazio">Nenhum membro adicionado.</p>
        )}
        <div className="cidade-form__team-grid">
          {form.team.map((member, index) => (
            <article className="cidade-form__team-card" key={member._id || `${member.name}-${index}`}>
              <div className="cidade-form__team-photo">
                {getImageUrl(member.photoUrl) ? (
                  <img src={getImageUrl(member.photoUrl)} alt={member.name} />
                ) : (
                  <span aria-hidden="true">{String(member.name || "M").charAt(0)}</span>
                )}
              </div>
              <div className="cidade-form__team-info">
                <strong>{member.name}</strong>
                <span>{member.role}</span>
              </div>
              <div className="cidade-form__team-actions">
                <button type="button" onClick={() => setMemberEditor({ index, member })}>
                  Editar
                </button>
                <button type="button" onClick={() => setMemberToRemove(index)}>
                  Excluir
                </button>
              </div>
            </article>
          ))}
        </div>
        <button
          type="button"
          className="cidade-form__adicionar"
          onClick={() => setMemberEditor({ index: null, member: null })}
        >
          + Adicionar membro
        </button>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Como ajudar</legend>
        <label className="cidade-form__campo">
          <span>Texto de chamada</span>
          <textarea
            rows={4}
            value={form.helpText}
            onChange={(e) => setField("helpText", e.target.value)}
            placeholder="Ex: Quer apoiar este nÃºcleo de perto? Fale com a nossa equipe."
          />
        </label>
      </fieldset>

      <fieldset className="cidade-form__secao">
        <legend>Contato</legend>
        <label className="cidade-form__campo">
          <span>Endereço</span>
          <input
            type="text"
            value={form.contact_info.address}
            onChange={(e) => setField("contact_info.address", e.target.value)}
          />
        </label>
        <label className="cidade-form__campo">
          <span>Telefone</span>
          <input
            type="text"
            value={form.contact_info.phone}
            onChange={(e) => setField("contact_info.phone", e.target.value)}
          />
        </label>
        <label className="cidade-form__campo">
          <span>WhatsApp</span>
          <input
            type="tel"
            value={form.contact_info.whatsapp}
            onChange={(e) => setField("contact_info.whatsapp", e.target.value)}
            placeholder="Ex: (35) 99999-9999"
          />
        </label>
        <label className="cidade-form__campo">
          <span>E-mail</span>
          <input
            type="text"
            inputMode="email"
            value={form.contact_info.email}
            onChange={(e) => setField("contact_info.email", e.target.value)}
          />
        </label>
        <label className="cidade-form__campo">
          <span>Horário de funcionamento</span>
          <input
            type="text"
            value={form.contact_info.openingHours}
            onChange={(e) =>
              setField("contact_info.openingHours", e.target.value)
            }
            placeholder="Ex: Seg a sex, 8h às 17h"
          />
        </label>
        <label className="cidade-form__campo">
          <span>Site</span>
          <input
            type="text"
            value={form.contact_info.website}
            onChange={(e) => setField("contact_info.website", e.target.value)}
            placeholder="https://..."
          />
        </label>
      </fieldset>

      {error && (
        <p className="cidade-form__erro" role="alert">
          {error}
        </p>
      )}

      <FormActions
        onCancel={onCancel}
        saveType="submit"
        saving={submitting}
        disabled={uploadingIndex !== null}
      />

      <MemberDialog
        open={Boolean(memberEditor)}
        member={memberEditor?.member}
        onCancel={() => setMemberEditor(null)}
        onSave={saveMember}
      />
      <ConfirmDialog
        open={memberToRemove !== null}
        title="Confirmar a exclusão?"
        description={
          memberToRemove !== null
            ? `O membro ${form.team[memberToRemove]?.name || "selecionado"} será removido da equipe.`
            : ""
        }
        onCancel={() => setMemberToRemove(null)}
        onConfirm={removeMember}
      />
    </form>
  );
}
